import { useCallback, useEffect, useMemo, useState } from "react";
import { scaleFromCenter } from "ol/extent";
import Feature from "ol/Feature";
import WKT from "ol/format/WKT";
import Geometry from "ol/geom/Geometry";
import Point from "ol/geom/Point";
import { useMapContext } from "@avinet/adaptive-ui-maps";

interface UseGeometryPicker {
  value: string | null;
  onValueChange: (wkt: string | null) => void;
  autoZoom?: boolean;
  useLocationAsValue?: boolean;
  mapSrid?: string;
  srid?: string;
}

export default function useGeometryPicker<T extends Geometry>({
  value,
  onValueChange,
  autoZoom = false,
  useLocationAsValue = false,
  mapSrid = "EPSG:3857",
  srid = "EPSG:4326",
}: UseGeometryPicker) {
  const { setCenterAndZoom, fitViewAndZoom } = useMapContext();

  const onChange = useCallback(
    (feature) => {
      if (!feature) {
        onValueChange(null);
        return;
      }
      const geom = feature.getGeometry().clone();
      geom.transform(mapSrid, srid);
      const wkt = new WKT().writeGeometry(geom);
      onValueChange(wkt || null);
    },
    [onValueChange, srid, mapSrid]
  );

  const onLocation = useCallback(
    (lon, lat) => {
      const geometry = new Point([lon, lat]);

      geometry.transform("EPSG:4326", mapSrid);
      setCenterAndZoom(geometry.getCoordinates(), 14);

      if (useLocationAsValue) {
        geometry.transform(mapSrid, srid);
        const wkt = new WKT().writeGeometry(geometry);
        onValueChange(wkt || null);
      }
    },
    [onValueChange, setCenterAndZoom, useLocationAsValue, srid, mapSrid]
  );

  const feature: Feature<T> | undefined = useMemo(() => {
    if (!value) return;
    try {
      const feature = new WKT().readFeature(value);
      const geometry = feature.getGeometry();
      if (geometry) geometry.transform(srid, mapSrid);
      return feature as Feature<T>;
    } catch (ex) {
      console.error(ex);
    }
  }, [value, srid, mapSrid]);

  const [initialValue] = useState(() => value);
  useEffect(() => {
    if (autoZoom && initialValue) {
      const feature = new WKT().readFeature(initialValue);
      const geometry = feature?.getGeometry();
      if (!geometry) return;

      geometry.transform(srid, mapSrid);

      if (geometry instanceof Point) {
        setCenterAndZoom(geometry.getCoordinates(), 14);
      } else {
        const extent = geometry.getExtent();
        scaleFromCenter(extent, 1.2);
        fitViewAndZoom(extent, 14);
      }
    }
  }, [autoZoom, initialValue, setCenterAndZoom, fitViewAndZoom, srid, mapSrid]);

  return { feature, onChange, onLocation };
}
